import React, { useEffect, useState } from "react";
import { GetCommanWay, CreateCommanWay, UpdateCommanWay, ListCommanWay, PostFormData } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";

const compactSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "1px 2px",
    minHeight: "32px",
    fontSize: "13px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "32px" }),
  option: (base: any, state: any) => ({
    ...base, backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b", fontSize: "13px", padding: "5px 10px",
  }),
  menu: (base: any) => ({ ...base, borderRadius: "10px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", zIndex: 9999 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "13px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "13px" }),
};

const MInput = ({ label, type = "text", value, onChange, placeholder, disabled, className = "", step }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[12px] font-medium text-slate-500 whitespace-nowrap min-w-[76px] text-right">{label}</label>
    <input
      type={type}
      value={value ?? ""}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder || label}
      step={step}
      className={`flex-1 min-w-0 px-2.5 py-[5px] rounded-lg border text-[13px] transition-all duration-200 outline-none
        ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200" : "bg-white border-slate-200 hover:border-primary2 focus:border-primary2 focus:ring-2 focus:ring-primary2/20"}
        text-slate-700 placeholder:text-slate-400`}
    />
  </div>
);

const MSelect = ({ label, options, value, onChange, isClearable, placeholder, className = "" }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[12px] font-medium text-slate-500 whitespace-nowrap min-w-[76px] text-right">{label}</label>
    <Select
      options={options}
      value={value}
      onChange={onChange}
      isClearable={isClearable}
      styles={compactSelectStyles}
      placeholder={placeholder || label}
      className="flex-1 min-w-0"
      menuPortalTarget={document.body}
    />
  </div>
);

const MTextarea = ({ label, value, onChange, placeholder, rows = 1, className = "" }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[12px] font-medium text-slate-500 whitespace-nowrap min-w-[76px] text-right">{label}</label>
    <textarea
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder || label}
      rows={rows}
      className="flex-1 min-w-0 px-2.5 py-[5px] rounded-lg border text-[13px] transition-all duration-200 outline-none bg-white border-slate-200 hover:border-primary2 focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700 placeholder:text-slate-400"
    />
  </div>
);

const categoryOptions = [
  { value: "printer", label: "Printer" },
  { value: "pc", label: "PC" },
  { value: "laptop", label: "Laptop" },
  { value: "vehicle", label: "Vehicle" },
  { value: "furniture", label: "Furniture" },
  { value: "other", label: "Other" },
];

const statusOptionsAll = [
  { value: "working", label: "Working" },
  { value: "not_working", label: "Not Working" },
  { value: "destroyed", label: "Destroyed" },
  { value: "sold", label: "Sold" },
  { value: "disposed", label: "Disposed" },
];

const depMethodOptions = [
  { value: "SLM", label: "Straight Line (SLM)" },
  { value: "WDV", label: "Written Down Value (WDV)" },
];

const AssetCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState<any>({
    name: "", category: null, brand: "", modelNo: "", serialNo: "", description: "",
    subbranch: null, assignedTo: null, purchaseDate: "", purchasePrice: "", vendorId: null,
    invoiceNo: "", invoiceFile: "", imageLinks: "", qty: "1",
    warrantyStartDate: "", warrantyEndDate: "", warrantyDetails: "",
    status: { value: "working", label: "Working" }, statusNote: "",
    usefulLifeYears: "", depreciationMethod: { value: "SLM", label: "Straight Line (SLM)" },
    depreciationPercent: "", residualValue: "",
    disposalDate: "", disposalPrice: "", disposalNote: "",
  });

  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [subbranchOptions, setSubbranchOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);

  useQuery(["subbranchListForAssetForm"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    onSuccess: (res: any) => {
      if (res?.status === "SUCCESS") setSubbranchOptions((res?.data?.data || res?.data?.docs || []).map((s: any) => ({ value: s.id, label: s.branch_name || s.name || `#${s.id}` })));
    },
  });

  useQuery(["userListForAssetForm"], () => ListCommanWay("/admin/user/list", { isActive: true }, { paginate: 10000 }), {
    onSuccess: (res: any) => {
      if (res?.status === "SUCCESS") setUserOptions((res?.data?.data || res?.data?.docs || []).map((u: any) => ({ value: u.id, label: u.name || u.email || `#${u.id}` })));
    },
  });

  useQuery(["vendorListForAssetForm"], () => ListCommanWay("/admin/vendor/list", {}, { paginate: 1000 }), {
    onSuccess: (res: any) => {
      if (res?.status === "SUCCESS") setVendorOptions((res?.data?.data || res?.data?.docs || []).map((v: any) => ({ value: v.id, label: v.vendor_name || v.companyName || `#${v.id}` })));
    },
  });

  const { isLoading: loadingAsset } = useQuery(["assetDetailForEdit", id], () => GetCommanWay(`/admin/asset/${id}`, {}, {}), {
    enabled: isEdit,
    onSuccess: (res: any) => {
      if (res?.status === "SUCCESS" && res?.data) {
        const d = res.data;
        setForm({
          name: d.name || "", brand: d.brand || "", modelNo: d.modelNo || "", serialNo: d.serialNo || "",
          description: d.description || "", qty: d.qty?.toString() || "1",
          category: d.category ? categoryOptions.find((c) => c.value === d.category) || { value: d.category, label: d.category } : null,
          subbranch: d.subbranch ? { value: d.subbranch, label: d._subbranch?.branch_name || `#${d.subbranch}` } : null,
          assignedTo: d.assignedTo ? { value: d.assignedTo, label: d._assignedTo?.name || `#${d.assignedTo}` } : null,
          purchaseDate: d.purchaseDate ? d.purchaseDate.split("T")[0] : "",
          purchasePrice: d.purchasePrice || "",
          vendorId: d.vendorId ? { value: d.vendorId, label: d._vendor?.name || d._vendor?.vendor_name || `#${d.vendorId}` } : null,
          invoiceNo: d.invoiceNo || "", invoiceFile: d.invoiceFile || "", imageLinks: d.imageLinks || "",
          warrantyStartDate: d.warrantyStartDate ? d.warrantyStartDate.split("T")[0] : "",
          warrantyEndDate: d.warrantyEndDate ? d.warrantyEndDate.split("T")[0] : "",
          warrantyDetails: d.warrantyDetails || "",
          status: statusOptionsAll.find((s) => s.value === d.status) || { value: "working", label: "Working" },
          statusNote: d.statusNote || "",
          usefulLifeYears: d.usefulLifeYears?.toString() || "",
          depreciationMethod: depMethodOptions.find((m) => m.value === d.depreciationMethod) || depMethodOptions[0],
          depreciationPercent: d.depreciationPercent?.toString() || "",
          residualValue: d.residualValue?.toString() || "",
          disposalDate: d.disposalDate ? d.disposalDate.split("T")[0] : "",
          disposalPrice: d.disposalPrice?.toString() || "",
          disposalNote: d.disposalNote || "",
        });
      }
    },
  });

  const { mutate: uploadInvoiceFile } = useMutation(
    (formData: FormData) => PostFormData("/admin/upload-to-azure", formData),
    {
      onMutate: () => setInvoiceUploading(true),
      onSuccess: (res: any) => {
        setInvoiceUploading(false);
        if (res?.status === "SUCCESS") { setForm((p: any) => ({ ...p, invoiceFile: res?.data?.uploadSuccess?.[0]?.path || "" })); UseToast("Uploaded", "success"); }
        else UseToast(res?.message || "Failed", "error");
      },
      onError: () => { setInvoiceUploading(false); UseToast("Upload failed", "error"); },
    }
  );

  const { mutate: uploadImageFile } = useMutation(
    (formData: FormData) => PostFormData("/admin/upload-to-azure", formData),
    {
      onMutate: () => setImageUploading(true),
      onSuccess: (res: any) => {
        setImageUploading(false);
        if (res?.status === "SUCCESS") {
          const paths = (res?.data?.uploadSuccess || []).map((u: any) => u.path).filter(Boolean);
          if (paths.length) {
            setForm((p: any) => {
              const existing = p.imageLinks ? p.imageLinks.split(",").map((l: string) => l.trim()).filter(Boolean) : [];
              return { ...p, imageLinks: [...existing, ...paths].join(",") };
            });
            UseToast("Uploaded", "success");
          }
        } else UseToast(res?.message || "Failed", "error");
      },
      onError: () => { setImageUploading(false); UseToast("Upload failed", "error"); },
    }
  );

  const handleInvoiceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { const fd = new FormData(); fd.append("files", f); fd.append("folderName", "assets_invoice"); uploadInvoiceFile(fd); }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) { const fd = new FormData(); Array.from(files).forEach((f) => fd.append("files", f)); fd.append("folderName", "assets_images"); uploadImageFile(fd); }
  };

  const [deletingImg, setDeletingImg] = useState<string | null>(null);

  const { mutate: deleteImageFromAzure } = useMutation(
    (url: string) => CreateCommanWay({ url: "/admin/delete-from-azure", data: { url } }),
    {
      onSuccess: (res: any, deletedUrl: string) => {
        if (res?.status === "SUCCESS") {
          setForm((p: any) => {
            const remaining = p.imageLinks.split(",").map((l: string) => l.trim()).filter((l: string) => l && l !== deletedUrl).join(",");
            return { ...p, imageLinks: remaining };
          });
          UseToast("Image deleted", "success");
        } else {
          UseToast(res?.message || "Delete failed", "error");
        }
        setDeletingImg(null);
      },
      onError: (err: any) => { UseToast(typeof err === "string" ? err : err?.message || "Delete failed", "error"); setDeletingImg(null); },
    }
  );

  const handleDeleteImage = (url: string) => {
    Swal.fire({ title: "Delete image?", text: "This will permanently remove the image.", icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Delete" }).then((r) => {
      if (r.isConfirmed) {
        setDeletingImg(url);
        deleteImageFromAzure(url);
      }
    });
  };

  const { mutate: createAsset, isLoading: creating } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/asset/create", data }),
    {
      onSuccess: (res: any) => { if (res?.status === "SUCCESS") { UseToast("Asset created", "success"); navigate("/asset/list"); } else UseToast(res?.message || "Failed", "error"); },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Error", "error"),
    }
  );

  const { mutate: updateAsset, isLoading: updating } = useMutation(
    (data: any) => UpdateCommanWay({ url: `/admin/asset/update/${id}`, data }),
    {
      onSuccess: (res: any) => { if (res?.status === "SUCCESS") { UseToast("Asset updated", "success"); navigate(`/asset/${id}`); } else UseToast(res?.message || "Failed", "error"); },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Error", "error"),
    }
  );

  const handleSubmit = () => {
    if (!form.name.trim()) { UseToast("Asset name is required", "error"); return; }
    if (isEdit && form.status?.value !== "working" && !form.statusNote.trim()) { UseToast("Status note required", "error"); return; }

    const payload: any = {
      name: form.name.trim(), category: form.category?.value || undefined, brand: form.brand.trim() || undefined,
      modelNo: form.modelNo.trim() || undefined, serialNo: form.serialNo.trim() || undefined,
      description: form.description.trim() || undefined, subbranch: form.subbranch?.value || undefined,
      assignedTo: form.assignedTo?.value || undefined, purchaseDate: form.purchaseDate || undefined,
      purchasePrice: form.purchasePrice ? parseFloat(form.purchasePrice) : undefined,
      vendorId: form.vendorId?.value || undefined, invoiceNo: form.invoiceNo.trim() || undefined,
      invoiceFile: form.invoiceFile || undefined, imageLinks: form.imageLinks.trim() || undefined,
      qty: form.qty ? parseInt(form.qty) : 1, warrantyStartDate: form.warrantyStartDate || undefined,
      warrantyEndDate: form.warrantyEndDate || undefined, warrantyDetails: form.warrantyDetails.trim() || undefined,
      status: form.status?.value || "working", statusNote: form.statusNote.trim() || undefined,
      usefulLifeYears: form.usefulLifeYears ? parseInt(form.usefulLifeYears) : undefined,
      depreciationMethod: form.depreciationMethod?.value || "SLM",
      depreciationPercent: form.depreciationPercent ? parseFloat(form.depreciationPercent) : undefined,
      residualValue: form.residualValue ? parseFloat(form.residualValue) : undefined,
    };
    if (form.status?.value === "sold" || form.status?.value === "disposed") {
      payload.disposalDate = form.disposalDate || undefined;
      payload.disposalPrice = form.disposalPrice ? parseFloat(form.disposalPrice) : undefined;
      payload.disposalNote = form.disposalNote.trim() || undefined;
    }
    isEdit ? updateAsset(payload) : createAsset(payload);
  };

  const u = (field: string, value: any) => setForm((p: any) => ({ ...p, [field]: value }));

  if (isEdit && loadingAsset) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1 min-h-screen">
      {/* Header */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => navigate("/asset/list")} className="text-slate-500 hover:text-slate-700 text-sm">← Back</button>
              <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
                {isEdit ? "Edit Asset" : "Add Asset"}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="flat" color="default" className="h-7 text-xs" onPress={() => navigate("/asset/list")}>Cancel</Button>
              <Button size="sm" color="primary" variant="solid" className="h-7 text-xs" onPress={handleSubmit} isLoading={creating || updating}>
                {isEdit ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Form — 2 columns on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
        {/* LEFT: General + Location + Status */}
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-xl px-3 py-2">
          <div className="grid grid-cols-3 md:grid-cols-1 gap-x-3 gap-y-1">
            <MInput label="Name *" value={form.name} onChange={(e: any) => u("name", e.target.value)} />
            <MSelect label="Category" options={categoryOptions} value={form.category} onChange={(v: any) => u("category", v)} isClearable />
            <MInput label="Brand" value={form.brand} onChange={(e: any) => u("brand", e.target.value)} />
            <MInput label="Model No" value={form.modelNo} onChange={(e: any) => u("modelNo", e.target.value)} />
            <MInput label="Serial No" value={form.serialNo} onChange={(e: any) => u("serialNo", e.target.value)} />
            <MInput label="Qty" type="number" value={form.qty} onChange={(e: any) => u("qty", e.target.value)} />
            <MSelect label="Branch" options={subbranchOptions} value={form.subbranch} onChange={(v: any) => u("subbranch", v)} isClearable />
            <MSelect label="Assigned To" options={userOptions} value={form.assignedTo} onChange={(v: any) => u("assignedTo", v)} isClearable />
            <MSelect label="Status" options={statusOptionsAll} value={form.status} onChange={(v: any) => u("status", v)} />
            {form.status?.value !== "working" && (
              <MInput label="Status Note" value={form.statusNote} onChange={(e: any) => u("statusNote", e.target.value)} className="col-span-2 md:col-span-1" />
            )}
            <MTextarea label="Description" value={form.description} onChange={(e: any) => u("description", e.target.value)} className="col-span-3 md:col-span-1" />
          </div>
          {(form.status?.value === "sold" || form.status?.value === "disposed") && (
            <>
              <hr className="my-1.5 border-slate-200" />
              <div className="grid grid-cols-3 md:grid-cols-1 gap-x-3 gap-y-1">
                <MInput label="Disposal Dt" type="date" value={form.disposalDate} onChange={(e: any) => u("disposalDate", e.target.value)} />
                <MInput label="Disposal ₹" type="number" value={form.disposalPrice} onChange={(e: any) => u("disposalPrice", e.target.value)} />
                <MInput label="Disp. Note" value={form.disposalNote} onChange={(e: any) => u("disposalNote", e.target.value)} />
              </div>
            </>
          )}
        </div>

        {/* RIGHT: Purchase + Warranty + Depreciation */}
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-xl px-3 py-2">
          <div className="grid grid-cols-3 md:grid-cols-1 gap-x-3 gap-y-1">
            <MInput label="Purchase Dt" type="date" value={form.purchaseDate} onChange={(e: any) => u("purchaseDate", e.target.value)} />
            <MInput label="Price ₹" type="number" value={form.purchasePrice} onChange={(e: any) => u("purchasePrice", e.target.value)} />
            <MSelect label="Vendor" options={vendorOptions} value={form.vendorId} onChange={(v: any) => u("vendorId", v)} isClearable />
            <MInput label="Invoice No" value={form.invoiceNo} onChange={(e: any) => u("invoiceNo", e.target.value)} />
            <div className="flex items-center gap-1.5">
              <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[72px] text-right">Inv. File</label>
              <input type="file" onChange={handleInvoiceUpload} className="flex-1 min-w-0 text-[11px] text-slate-600 file:mr-2 file:py-[2px] file:px-2 file:rounded-lg file:border-0 file:text-[11px] file:font-medium file:bg-blue-50 file:text-blue-700" />
              {invoiceUploading && <Spinner />}
              {form.invoiceFile && <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[72px] text-right">Images</label>
              <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="flex-1 min-w-0 text-[11px] text-slate-600 file:mr-2 file:py-[2px] file:px-2 file:rounded-lg file:border-0 file:text-[11px] file:font-medium file:bg-blue-50 file:text-blue-700" />
              {imageUploading && <Spinner />}
            </div>
          </div>
          <hr className="my-1.5 border-slate-200" />
          <div className="grid grid-cols-3 md:grid-cols-1 gap-x-3 gap-y-1">
            <MInput label="Warranty St" type="date" value={form.warrantyStartDate} onChange={(e: any) => u("warrantyStartDate", e.target.value)} />
            <MInput label="Warranty End" type="date" value={form.warrantyEndDate} onChange={(e: any) => u("warrantyEndDate", e.target.value)} />
            <MInput label="Wrnty Detail" value={form.warrantyDetails} onChange={(e: any) => u("warrantyDetails", e.target.value)} />
          </div>
          <hr className="my-1.5 border-slate-200" />
          <div className="grid grid-cols-3 md:grid-cols-1 gap-x-3 gap-y-1">
            <MSelect label="Dep Method" options={depMethodOptions} value={form.depreciationMethod} onChange={(v: any) => u("depreciationMethod", v)} />
            <MInput label="Dep Rate %" type="number" step="0.01" value={form.depreciationPercent} onChange={(e: any) => u("depreciationPercent", e.target.value)} />
            <MInput label="Useful Life" type="number" value={form.usefulLifeYears} onChange={(e: any) => u("usefulLifeYears", e.target.value)} placeholder="Years" />
            <MInput label="Residual ₹" type="number" value={form.residualValue} onChange={(e: any) => u("residualValue", e.target.value)} />
          </div>
        </div>
      </div>

      {/* Image previews */}
      {form.imageLinks && (
        <div className="mt-2 bg-white/80 backdrop-blur-sm rounded-xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap gap-3">
            {form.imageLinks.split(",").map((url: string, i: number) => url.trim() && (
              <div key={i} className="relative group">
                <a href={url.trim()} target="_blank" rel="noopener noreferrer">
                  <img src={url.trim()} alt={`${i + 1}`} className={`w-20 h-20 object-cover border rounded-lg ${deletingImg === url.trim() ? "opacity-40" : ""}`} />
                </a>
                <button
                  onClick={() => handleDeleteImage(url.trim())}
                  disabled={!!deletingImg}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetCreate;
